import { useMemo } from "react";
import {
  MatrixTable,
  type MatrixTableColumn,
  type MatrixTableGroupColumn,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import type { Interval } from "@models/ISchema";
import type { AnnualScheduleGroup, AnnualScheduleRow } from "../../adapters";
import type { AnnualScheduleCellSelection, AnnualScheduleWeek } from "../../types";
import LeagueTeachers from "../league-teachers";
import AnnualScheduleCell from "../annual-schedule-cell";
import AnnualScheduleComponentPill from "../annual-schedule-component-pill";
import styles from "./annualScheduleMatrix.module.scss";
import { annualScheduleColumnWidths } from "../../lib";
import { useAnnualScheduleLayout } from "../../hooks/use-annual-schedule-layout";

interface Props {
  groups: AnnualScheduleGroup[];
  weeks: AnnualScheduleWeek[];
  onSelectCell: (selection: AnnualScheduleCellSelection) => void;
}

/** Formats the selected week's ISO dates for the existing weekly-cell tooltip copy. */
export const weekDateRange = (week: Interval) =>
  `${week.startingDate?.slice(0, 10) ?? ""} – ${week.endingDate?.slice(0, 10) ?? ""}`;

/**
 * Displays complete package leagues with one component row per configured component.
 *
 * @param props - Visible league groups, catalog weeks, and the cell-selection action.
 * @returns A horizontally scrollable MatrixTable with fixed identity columns.
 *
 * @remarks A table group represents a package `Link`, not a backend `Group`.
 * Positive block counts select a league/component/week tuple; empty cells stay inert.
 */
export default function AnnualScheduleMatrix({ groups, weeks, onSelectCell }: Props) {
  const { t } = useTranslation();
  const { containerRef, weekWidths } = useAnnualScheduleLayout(weeks);
  const prefix = "dimension.package.annual";
  const groupColumns = useMemo<MatrixTableGroupColumn<AnnualScheduleGroup>[]>(
    () => [
      {
        key: "subject",
        header: t(`${prefix}.league`),
        width: annualScheduleColumnWidths.subject,
        pinned: "start",
        renderGroupCell: group => (
          <div className={styles.annualScheduleMatrix_identity}>
            <span className={styles.annualScheduleMatrix_name}>
              {group.course?.name || group.course?.code}
            </span>
            <span className={styles.annualScheduleMatrix_detail}>
              {group.course?.code} · {t(`${prefix}.league`)}: {group.code}
            </span>
          </div>
        ),
      },
      {
        key: "teacher",
        header: t(`${prefix}.teacher`),
        width: annualScheduleColumnWidths.teacher,
        pinned: "start",
        renderGroupCell: group => <LeagueTeachers group={group} />,
      },
    ],
    [t],
  );
  const columns = useMemo<MatrixTableColumn<AnnualScheduleGroup, AnnualScheduleRow>[]>(
    () => [
      {
        key: "component",
        header: t(`${prefix}.component`),
        width: annualScheduleColumnWidths.component,
        pinned: "start",
        isRowHeader: true,
        renderCell: (row, group) => (
          <AnnualScheduleComponentPill
            label={row.label}
            colorIndex={(group.rows.findIndex(item => item.id === row.id) % 17) + 1}
          />
        ),
      },
      ...weeks.map((week, index) => ({
        key: week.id,
        width: weekWidths[index],
        align: "center" as const,
        header: week.name,
        renderCell: (row: AnnualScheduleRow, group: AnnualScheduleGroup) => {
          const count = row.counts.get(week.id) ?? 0;
          return count > 0 ? (
            <AnnualScheduleCell
              count={count}
              week={week}
              label={t(`${prefix}.cell-label`, {
                subject: group.course?.name || group.course?.code,
                league: group.code,
                component: row.label,
                week: week.name,
                dates: weekDateRange(week),
                count,
              })}
              onSelect={() =>
                onSelectCell({ leagueId: group.id, componentId: row.id, intervalId: week.id })
              }
            />
          ) : null;
        },
      })),
    ],
    [weeks, weekWidths, onSelectCell, t],
  );

  return (
    <div ref={containerRef} className={styles.annualScheduleMatrix}>
      <MatrixTable
        groups={groups}
        groupColumns={groupColumns}
        columns={columns}
        getGroupKey={group => group.id}
        getRows={group => group.rows}
        getRowKey={row => row.id}
        accessibleLabel={t(`${prefix}.title`)}
      />
    </div>
  );
}
